import { useEffect } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Alert } from "@mui/material";

import { PlantLabour } from "../../../../../api/types/plants";
import { useApiValidation } from "../../../../../hooks/useApiValidation";
import { useFetchData } from "../../../../../hooks/useFetchData";
import { SectionProps } from "../../../../../types/section-props";
import { useLabourData } from "../../../api/useLabourData";
import { useLabourUpdate } from "../../../api/useLabourUpdate";
import { CafsSection } from "../../../const/cafs-section";
import { useSectionSaveDisabled } from "../../../hooks/useSectionSaveDisabled";
import { mapInitialValues } from "../../../utils/map-initial-values";
import { renderCafsTableRow } from "../../../utils/render-cafs-table-row";
import { CafsTable } from "../../cafs-table/CafsTable";
import { SectionRemarks } from "../SectionRemarks";

import { labourSchema } from "./labour-schema";
import { useLaboursVariables } from "./useLabourVariables";

export function LabourSection({ expanded }: SectionProps) {
  const variables = useLaboursVariables();

  const { shouldFetchData } = useFetchData(expanded);
  const { labourData, isPending } = useLabourData(shouldFetchData);
  const { updateLabour, apiErrors } = useLabourUpdate({
    onSuccess: () => {},
  });

  const methods = useForm<FieldValues>({
    resolver: yupResolver(labourSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (labourData) {
      methods.reset(mapInitialValues(labourData, variables));
    }
  }, [labourData]);

  const handleSubmit = (data: FieldValues) => {
    updateLabour(data);
  };

  useApiValidation(apiErrors, methods);

  const { disabled } = useSectionSaveDisabled(methods, isPending);

  return labourData && !isPending ? (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <SectionRemarks<PlantLabour> name="LabourRemarks" data={labourData} />
        <CafsTable<PlantLabour>
          section={CafsSection.PlantLabour}
          rows={variables}
          render={renderCafsTableRow}
          data={labourData ?? {}}
          saveDisabled={disabled}
        />
      </form>
    </FormProvider>
  ) : (
    <div className="w-full">
      <Alert severity="info">Loading data</Alert>
    </div>
  );
}
